import {
  useCallback,
  useEffect,
  useState
} from "react";
import { AddVariableModal } from "../addVariableModal/addVariableModal";
import {
  CreateVariable,
  CreateVariableFirstStep
} from "../../../types/variableType";
import { DefineDefaultValues } from "../defineDefaultValues/defineDefaultValues";
import { useCreateVariableMutation } from "../../../api/variableService";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { setShowSpinner } from "../../../store/slices/mainSlice";
import { useGetAllEnvironmentsQuery } from "../../../api/environmentService";
import { findProjectSettings } from "../../../store/selectors";

type SwitcherAddVariableModalProps = {
  onClose: (result?: { variableId: number, variableValueId: number }) => void;
}

export const SwitcherAddVariableModal = ({onClose}: SwitcherAddVariableModalProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);

  const [createVariableFirstStep, setCreateVariableFirstStep] = useState<CreateVariableFirstStep>();
  const [isRefetching, setIsRefetching] = useState(false);

  const { data: environments, refetch: refetchEnvironments } = useGetAllEnvironmentsQuery("");
  const [createVariableMutation, { data: createVariableId, isLoading: isLoadingCreate }] = useCreateVariableMutation();

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingCreate));
  }, [dispatch, isLoadingCreate]);

  useEffect(() => {
    if (createVariableId && environments) {
      refetchEnvironments().unwrap().then(() => {
        setIsRefetching(true);
      });
    }
  }, [createVariableId, refetchEnvironments]);

  useEffect(() => {
    if (isRefetching && environments && createVariableId) {
      const environment = environments.find(environment => environment.id === selectedEnvironment.id);
      const defaultVariableValue = environment?.defaultVariableValues.find(defaultVariableValue => defaultVariableValue.variableId === createVariableId);
      onClose({ variableId: createVariableId, variableValueId: defaultVariableValue?.variableValueId || 0 });
    }
  }, [isRefetching, environments, createVariableId, selectedEnvironment]);

  const onCloseAddVariableModal = useCallback((newVariable?: CreateVariableFirstStep) => {
    if (!!newVariable) {
      if (!environments?.length) {
        createVariableMutation({
          ...newVariable,
          environmentValues: []
        });
      } else if (newVariable.values.length === 1) {
        const createVariable: CreateVariable = {
          ...newVariable,
          environmentValues: environments.map(environment => ({
            environmentId: environment.id,
            variableValue: newVariable.values[0].value
          }))
        }
        createVariableMutation(createVariable);
      } else {
        setCreateVariableFirstStep(newVariable);
      }
    } else {
      onClose();
    }
  }, [setCreateVariableFirstStep, onClose, createVariableMutation, environments]);

  const onCloseDefineDefaultValues = useCallback((newVariable?: CreateVariable) => {
    if (!!newVariable) {
      createVariableMutation(newVariable);
    } else {
      onClose();
    }
  }, [onClose, createVariableMutation])

  return !createVariableFirstStep
    ? <AddVariableModal onClose={onCloseAddVariableModal}
                        environments={environments}/>
    : <DefineDefaultValues firstStepVariable={createVariableFirstStep}
                            onClose={onCloseDefineDefaultValues}
                            environments={environments} />;
}